import React, { useCallback } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { DATE_LONG_FORMAT_WITH_YEAR_AND_DAYOFWEEK } from '../../../../data/constants/datetime';
import { useEvent } from '../../../../data/hooks/eventHooks';
import { useI18n } from '../../../../data/hooks/i18nHooks';
import { useProgramSession } from '../../../../data/hooks/schedule/useProgramSession';
import { eventMomentFactory } from '../../../../data/misc/eventMomentFactory';
import resolvePath from '../../../../misc/resolvePath';
import Scrollable from '../../../elements/Scrollable';
import TopBarEvent from '../../../elements/TopBar/TopBarEvent';
import UserTimeInfo from '../../../elements/UserTimeInfo/UserTimeInfo';
import LoadProgram from '../../../loaders/LoadProgram';
import RedirectOnError from '../../../validate/RedirectOnError';
import WaitWhileBusy from '../../../WaitWhileBusy';
import Screen from '../../Screen';
import chooseSession from '../common/handleChooseSession';
import leaveSession from '../common/handleDeselectSession';
import RateScheduleItem from '../common/RateScheduleItem';
import SpeakersList from '../common/SpeakersList/SpeakersList';
import SpeakersMosaic from '../common/SpeakersMosaic/SpeakersMosaic';
import ScheduleContentButton from './elements/ScheduleContentButton';
import ScheduleItemDescription from './elements/ScheduleItemDescription';
import ScheduleItemFullDescription from './elements/ScheduleItemFullDescription';
import ScheduleItemFullInfo from './elements/ScheduleItemFullInfo';
import ScheduleItemImpliedSessions from './elements/ScheduleItemImpliedSessions';
import ScheduleItemSelectionStaticNotification from './elements/ScheduleItemSelectionStaticNotification';
import ScheduleItemVote from './elements/ScheduleItemVote';
import ScheduleSessionBaseInfo from './elements/ScheduleSessionBaseInfo';
import TaskAssigneesList from './elements/TaskAssigneesList';
import ScheduleItemTags from './ScheduleItemTags/ScheduleItemTags';
import SelectableSessionControls from './SelectableSessionControls';
import { useOtherSessions } from './useOtherSessions';

export function EventScheduleItemScreen(props) {
    const {} = props,
        { eventId, itemId } = useParams(),
        event = useEvent(eventId),
        session = useProgramSession(eventId, itemId),
        // implied = useProgramImpliedSessions(eventId, itemId),
        otherSessionsFilter = useOtherSessions(event, session),
        // inSameDisplayCategory = useSessionsInSameDisplayCategory(event, programItem),
        // readOnly = !event.joined,
        // isVotingOpen = useLiveVotingSession(eventId, session.vote_id)?.is_open,
        // [busy, setBusy] = useState(false),
        startMoment = eventMomentFactory(event, session?.start_local),
        i18n = useI18n();

    const handleChooseSession = useCallback(
            (e) => {
                e.preventDefault();
                chooseSession(i18n, event.id, session, otherSessionsFilter, {
                    onStart: () => setBusy(true),
                    onEnd: () => setBusy(false),
                });
            },
            [i18n, event, session, otherSessionsFilter]
        ),
        handleLeaveSession = useCallback(() => {
            leaveSession(event.id, session, {
                onStart: () => setBusy(true),
                onEnd: () => setBusy(false),
            });
        }, [event, session]);

    let fallback = false;
    if (!event) fallback = true;
    else if (!session) fallback = true;
    else if (event.settings.disable_schedule) fallback = true;

    if (fallback) return <Redirect to={resolvePath(location.pathname, '..')} />;

    const header = (
            <>
                <TopBarEvent event={event} eventMapIcon>{startMoment?.format(DATE_LONG_FORMAT_WITH_YEAR_AND_DAYOFWEEK)}</TopBarEvent>
                <hr />
            </>
        ),
        footer = (
            <div style={{ padding: 10 }}>
                <ScheduleContentButton event={event} session={session} />
            </div>
        );

    return (
        <Screen header={header} footer={footer}>
            <Scrollable>
                <ScheduleItemSelectionStaticNotification session={session} />
                <ScheduleSessionBaseInfo event={event} session={session} />
                <UserTimeInfo event={event} time={session.total_start_local ?? session.start_local} />
                <ScheduleItemImpliedSessions event={event} session={session} />
                <SelectableSessionControls event={event} session={session} onJoin={handleChooseSession} onLeave={handleLeaveSession} />
                <ScheduleItemFullInfo event={event} session={session} />
                <ScheduleItemDescription event={event} session={session} />
                {session.display_type == 'speeddate' ? <SpeakersMosaic event={event} session={session} /> : <SpeakersList event={event} session={session} />}
                <TaskAssigneesList event={event} session={session} />
                <ScheduleItemTags event={event} session={session} />
                <ScheduleItemVote event={event} session={session} />
                <RateScheduleItem event={event} session={session} />
                <ScheduleItemFullDescription session={session} />
            </Scrollable>
        </Screen>
    );
}

export default (props) => (
    <LoadProgram eventId={props.match.params.eventId}>
        <WaitWhileBusy>
            <RedirectOnError>
                <EventScheduleItemScreen />
            </RedirectOnError>
        </WaitWhileBusy>
    </LoadProgram>
);
